import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * License Page
 *
 * Full license management with activation, deactivation, and status display.
 * Uses inline styles for WordPress CSS isolation.
 */
import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useLicense } from '../hooks/useLicense';
import { Key, Shield, CheckCircle, XCircle, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
// Inline styles for WordPress CSS isolation
const styles = {
    page: {
        maxWidth: '768px',
        margin: '0 auto',
        padding: '32px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
        lineHeight: '1.5',
    },
    header: {
        marginBottom: '32px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px',
    },
    titleIcon: {
        width: '28px',
        height: '28px',
        color: '#6366f1',
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        marginTop: '8px',
    },
    errorBox: {
        padding: '16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        color: '#dc2626',
        marginBottom: '24px',
        fontSize: '14px',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        marginBottom: '24px',
    },
    cardHeader: {
        padding: '20px 24px',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#111827',
    },
    cardBody: {
        padding: '24px',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '9999px',
        fontSize: '14px',
        fontWeight: 500,
    },
    badgeActive: {
        backgroundColor: '#dcfce7',
        color: '#166534',
    },
    badgeExpired: {
        backgroundColor: '#fef2f2',
        color: '#dc2626',
    },
    badgeInactive: {
        backgroundColor: '#f3f4f6',
        color: '#4b5563',
    },
    badgeTrial: {
        backgroundColor: '#fef9c3',
        color: '#a16207',
    },
    badgeIcon: {
        width: '16px',
        height: '16px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px',
        marginBottom: '20px',
    },
    gridItem: {
        padding: '0',
    },
    gridLabel: {
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '4px',
    },
    gridValue: {
        fontSize: '15px',
        fontWeight: 500,
        color: '#111827',
    },
    gridValueMono: {
        fontSize: '14px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        color: '#111827',
    },
    buttonGroup: {
        display: 'flex',
        gap: '12px',
        paddingTop: '20px',
        borderTop: '1px solid #f3f4f6',
    },
    buttonSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
    },
    buttonDanger: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#dc2626',
        backgroundColor: '#ffffff',
        border: '1px solid #fca5a5',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
    },
    buttonPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#ffffff',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    buttonIcon: {
        width: '16px',
        height: '16px',
    },
    trialBox: {
        padding: '16px',
        backgroundColor: '#fef9c3',
        border: '1px solid #fde047',
        borderRadius: '12px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
    },
    trialIcon: {
        width: '20px',
        height: '20px',
        color: '#a16207',
        flexShrink: 0,
        marginTop: '2px',
    },
    trialContent: {
        flex: 1,
    },
    trialTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#a16207',
        marginBottom: '4px',
    },
    trialText: {
        fontSize: '14px',
        color: '#92400e',
    },
    formGroup: {
        marginBottom: '24px',
    },
    formLabel: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
        marginBottom: '8px',
    },
    inputGroup: {
        display: 'flex',
        gap: '12px',
    },
    input: {
        flex: 1,
        padding: '10px 16px',
        fontSize: '14px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        color: '#111827',
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        borderRadius: '10px',
        outline: 'none',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    },
    inputFocus: {
        borderColor: '#6366f1',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
    },
    errorText: {
        fontSize: '13px',
        color: '#dc2626',
        marginTop: '8px',
    },
    link: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
        textDecoration: 'none',
        paddingTop: '16px',
        borderTop: '1px solid #f3f4f6',
    },
    linkIcon: {
        width: '16px',
        height: '16px',
    },
    featuresCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        padding: '24px',
    },
    featuresTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '20px',
    },
    featuresList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        fontSize: '15px',
        color: '#374151',
        borderBottom: '1px solid #f3f4f6',
    },
    featureItemLast: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        fontSize: '15px',
        color: '#374151',
        borderBottom: 'none',
    },
    featureIconActive: {
        width: '20px',
        height: '20px',
        color: '#22c55e',
    },
    featureIconInactive: {
        width: '20px',
        height: '20px',
        color: '#d1d5db',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
    },
    spinner: {
        width: '32px',
        height: '32px',
        color: '#9ca3af',
        animation: 'spin 1s linear infinite',
    },
};
// CSS keyframes for spinner (injected via style tag)
const spinnerKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
export function License() {
    const { t } = useTranslation();
    const { status, loading, error, activate, deactivate, revalidate } = useLicense();
    const [keyInput, setKeyInput] = useState('');
    const [activating, setActivating] = useState(false);
    const [activationError, setActivationError] = useState(null);
    const [inputFocused, setInputFocused] = useState(false);
    const handleActivate = async () => {
        if (!keyInput.trim())
            return;
        setActivating(true);
        setActivationError(null);
        const result = await activate(keyInput);
        if (!result.success) {
            setActivationError(result.error || 'Activation failed');
        }
        else {
            setKeyInput('');
        }
        setActivating(false);
    };
    const handleDeactivate = async () => {
        if (confirm(t('confirmDeactivate') || 'Are you sure you want to deactivate this license?')) {
            await deactivate();
        }
    };
    const formatDate = (dateString) => {
        if (!dateString)
            return '-';
        return new Date(dateString).toLocaleDateString();
    };
    const getStatusBadge = () => {
        if (!status)
            return null;
        const badges = {
            active: { style: styles.badgeActive, icon: CheckCircle, text: t('licenseActive') || 'License Active' },
            expired: { style: styles.badgeExpired, icon: XCircle, text: t('licenseExpired') || 'Expired' },
            inactive: { style: styles.badgeInactive, icon: AlertCircle, text: t('licenseInactive') || 'Inactive' },
            trial: { style: styles.badgeTrial, icon: AlertCircle, text: t('licenseTrial') || 'Trial' },
        };
        const badge = badges[status.status] || badges.inactive;
        const Icon = badge.icon;
        return (_jsxs("span", { style: { ...styles.badge, ...badge.style }, children: [_jsx(Icon, { style: styles.badgeIcon }), badge.text] }));
    };
    const proFeatures = [
        t('featureUnlimitedAI') || 'Unlimited AI conversations',
        t('featureAdvancedRAG') || 'Advanced RAG with Supabase',
        t('featurePlaybooks') || 'Custom playbooks',
        t('featureAnalytics') || 'Analytics dashboard',
        t('featureSupport') || 'Priority support',
    ];
    if (loading && !status) {
        return (_jsxs(_Fragment, { children: [_jsx("style", { children: spinnerKeyframes }), _jsx("div", { style: styles.loadingContainer, children: _jsx(RefreshCw, { style: styles.spinner }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: spinnerKeyframes }), _jsxs("div", { style: styles.page, children: [_jsxs("div", { style: styles.header, children: [_jsxs("h1", { style: styles.title, children: [_jsx(Key, { style: styles.titleIcon }), t('license') || 'License & Updates'] }), _jsx("p", { style: styles.subtitle, children: t('licenseDescription') || 'Manage your WooAI Chatbot Pro license' })] }), error && _jsx("div", { style: styles.errorBox, children: error }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.cardHeader, children: [_jsx("h2", { style: styles.cardTitle, children: t('licenseStatus') || 'License Status' }), getStatusBadge()] }), _jsx("div", { style: styles.cardBody, children: (status?.is_active || status?.is_usable) ? (
                                /* Active License */
                                _jsxs(_Fragment, { children: [_jsxs("div", { style: styles.grid, children: [_jsxs("div", { style: styles.gridItem, children: [_jsx("div", { style: styles.gridLabel, children: t('registeredTo') || 'Registered to' }), _jsx("div", { style: styles.gridValue, children: status.domain || '-' })] }), _jsxs("div", { style: styles.gridItem, children: [_jsx("div", { style: styles.gridLabel, children: t('expiresOn') || 'Expires on' }), _jsx("div", { style: styles.gridValue, children: formatDate(status.expires_at) })] }), _jsxs("div", { style: styles.gridItem, children: [_jsx("div", { style: styles.gridLabel, children: t('licenseKey') || 'License Key' }), _jsx("div", { style: styles.gridValueMono, children: status.license_key || '-' })] }), _jsxs("div", { style: styles.gridItem, children: [_jsx("div", { style: styles.gridLabel, children: t('plan') || 'Plan' }), _jsx("div", { style: { ...styles.gridValue, textTransform: 'capitalize' }, children: status.plan })] })] }), _jsxs("div", { style: styles.buttonGroup, children: [_jsxs("button", { onClick: () => revalidate(), disabled: loading, style: {
                                                        ...styles.buttonSecondary,
                                                        ...(loading ? styles.buttonDisabled : {}),
                                                    }, children: [_jsx(RefreshCw, { style: { ...styles.buttonIcon, ...(loading ? { animation: 'spin 1s linear infinite' } : {}) } }), t('revalidate') || 'Revalidate'] }), _jsx("button", { onClick: handleDeactivate, disabled: loading, style: {
                                                        ...styles.buttonDanger,
                                                        ...(loading ? styles.buttonDisabled : {}),
                                                    }, children: t('deactivateLicense') || 'Deactivate License' })] })] })) : (
                                /* Trial / Inactive */
                                _jsxs(_Fragment, { children: [status?.status === 'trial' && status.trial_days_left !== undefined && (_jsxs("div", { style: styles.trialBox, children: [_jsx(AlertCircle, { style: styles.trialIcon }), _jsxs("div", { style: styles.trialContent, children: [_jsx("div", { style: styles.trialTitle, children: t('licenseTrial') || 'Trial Mode' }), _jsx("div", { style: styles.trialText, children: t('trialDaysLeft', { days: status.trial_days_left }) || `${status.trial_days_left} days left in trial` })] })] })), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.formLabel, children: t('enterLicenseKey') || 'Enter your license key' }), _jsxs("div", { style: styles.inputGroup, children: [_jsx("input", { type: "text", value: keyInput, onChange: (e) => setKeyInput(e.target.value.toUpperCase()), onFocus: () => setInputFocused(true), onBlur: () => setInputFocused(false), placeholder: "XXXX-XXXX-XXXX-XXXX", style: {
                                                                ...styles.input,
                                                                ...(inputFocused ? styles.inputFocus : {}),
                                                            }, maxLength: 19 }), _jsxs("button", { onClick: handleActivate, disabled: activating || !keyInput.trim(), style: {
                                                                ...styles.buttonPrimary,
                                                                ...(activating || !keyInput.trim() ? styles.buttonDisabled : {}),
                                                            }, children: [activating ? (_jsx(RefreshCw, { style: { ...styles.buttonIcon, animation: 'spin 1s linear infinite' } })) : (_jsx(Shield, { style: styles.buttonIcon })), t('activateLicense') || 'Activate License'] })] }), activationError && _jsx("p", { style: styles.errorText, children: activationError })] }), _jsxs("a", { href: "https://leowebmarketing.pro/woo-ai-chatbot-pro", target: "_blank", rel: "noopener noreferrer", style: styles.link, children: [t('getPro') || 'Get Pro License', _jsx(ExternalLink, { style: styles.linkIcon })] })] })) })] }), _jsxs("div", { style: styles.featuresCard, children: [_jsx("h3", { style: styles.featuresTitle, children: t('proFeatures') || 'Pro Features' }), _jsx("ul", { style: styles.featuresList, children: proFeatures.map((feature, i) => (_jsxs("li", { style: i === proFeatures.length - 1 ? styles.featureItemLast : styles.featureItem, children: [_jsx(CheckCircle, { style: status?.is_active ? styles.featureIconActive : styles.featureIconInactive }), feature] }, i))) })] })] })] }));
}
export default License;
